import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { ButtonElement, ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { RenderOrientation } from "appworks/graphics/pixi/render-orientation";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { uiFlags } from "appworks/ui/flags/ui-flags";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { slotModel } from "slotworks/model/slot-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

import { AbstractControl } from "../../abstract-control";

/**
 * Class representing a bet menu control.
 */
export class BetMenuControl extends AbstractControl {
    protected readonly buttonGapAmount: number = 25;
    protected readonly scrollEpsilon: number = 5;

    protected readonly currentLabelName: string = "current_label";
    protected readonly currentBetTranslation: string = "current_bet";
    protected readonly stakeButtonPrefix: string = "stake_btn_";
    protected readonly menuMaskName: string = "menu_mask";
    protected readonly menuBackgroundName: string = "clickblocker";
    protected readonly fadeSpriteNames: string[] = ["fade_top", "fade_bottom"];
    protected readonly scrollSpriteNames: string[] = ["scroll_track", "scroll_bar"];

    protected scrollBarSprite: Sprite = undefined;
    protected scrollTrackSprite: Sprite = undefined;
    protected activeScrollObject: Sprite | ButtonElement = undefined;
    protected fadeSpriteTop: Sprite = undefined;
    protected fadeSpriteBottom: Sprite = undefined;
    protected scrolledAmount = 0;
    protected previousY = 0;
    protected minScrollY = 0;
    protected maxScrollY = 0;
    protected scrollBarTopY = 0;
    protected scrollBarBottomY = 0;
    protected displayHeight = 0;

    protected scrollWheelCallback: (event: WheelEvent) => void = undefined;
    protected orientationChangeCallback: () => void;

    /**
     * Constructor for the BetMenuControl class.
     * @param {Layers} layer - The active game layer.
     * @param {ButtonElement} stakeButton - A generic stake button element.
     */
    constructor(protected layer: Layers, protected stakeButton: ButtonElement) {
        super();
        this.regenerateMenu();
        this.turnOnScrolling();

        this.orientationChangeCallback = () => {
            this.regenerateMenu();
        };
        Services.get(CanvasService).onOrientationChange.add(this.orientationChangeCallback, this);
    }

    /**
     * Sets the enabled state of the control. This does not get disabled.
     * It will get destroyed when it's no longer used.
     * @param {boolean} _ - Unused/discard.
     */
    public override setEnabled(_: boolean): void {
        // Never disable
    }

    /**
     * Called when the control is destroyed. Removes the event listener
     * for scroll wheel events.
     */
    public override destroy(): void {
        if (this.scrollWheelCallback) {
            window.removeEventListener("wheel", this.scrollWheelCallback);
            this.scrollWheelCallback = undefined;
        }

        Services.get(CanvasService).onOrientationChange.remove(this.orientationChangeCallback, this);
    }

    /**
     * Regenerates the bet menu from scratch by adding the buttons dynamically based on
     * the slot model's configured stakes.
     */
    protected regenerateMenu(): void {
        const stakes = slotModel.read().bet.creditsPerLine.values;

        // Start with fresh buttons every time
        this.removeElementsFromLayer(stakes.map((eachStake) => `${this.stakeButtonPrefix}${eachStake}`));

        // Remove these elements and add back later to guarantee correct layering in the container
        [this.fadeSpriteTop, this.fadeSpriteBottom] = this.removeElementsFromLayer(this.fadeSpriteNames);
        [this.scrollTrackSprite, this.scrollBarSprite] = this.removeElementsFromLayer(this.scrollSpriteNames).map((eachElement) =>
            eachElement.clone()
        );

        // Display the correct current stake
        const currentStake = Services.get(SlotBetService).getTotalStake();
        this.updateCurrentStakeLabel(currentStake);

        const buttonsPerRow = RenderOrientation.isLandscape() ? 3 : 2;

        // Determine and set allowable scroll range
        [this.minScrollY, this.maxScrollY] = this.getMinMaxScroll(stakes, buttonsPerRow);

        // Generate buttons for each configured stake using the template stake button
        this.stakeButton.setVisible(true);
        this.generateStakeButtons(stakes, currentStake, buttonsPerRow);
        this.stakeButton.setVisible(false);

        // When menu is regenerated, top fade will always be fully transparent
        this.fadeSpriteTop.alpha = 0.0;

        // Add removed fades back to the layer
        this.layer.add(this.fadeSpriteTop);
        this.layer.add(this.fadeSpriteBottom);

        // Add removed scroll elements back to the layer
        if (this.scrollBarSprite && this.scrollTrackSprite) {
            this.layer.add(this.scrollTrackSprite);
            this.layer.add(this.scrollBarSprite);

            this.scrollBarSprite.interactive = true;
            this.scrollTrackSprite.interactive = true;
            this.addScrollEventsToDisplayObject(this.scrollBarSprite, true);

            // Set top and bottom scroll bar positions
            [this.scrollBarTopY, this.scrollBarBottomY] = this.getScrollBarTopBottom(this.scrollTrackSprite, this.scrollBarSprite);
        }
    }

    /**
     * Updates the current stake label with the provided stake value.
     * @param {number} currentStake - The current stake value.
     */
    protected updateCurrentStakeLabel(currentStake: number): void {
        if (Object.prototype.hasOwnProperty.call(this.layer.allElements, this.currentLabelName)) {
            const shouldDisplayTrailingDecimals = gameiomModel.shouldDisplayCredits() ? false : gameiomModel.displayWholeStakeAsDecimal;
            const currentStakeText = Services.get(CurrencyService).format(
                currentStake,
                shouldDisplayTrailingDecimals,
                gameiomModel.shouldDisplayCredits()
            );
            this.layer.allElements[this.currentLabelName].text = `${Services.get(TranslationsService).get(
                this.currentBetTranslation
            )}${currentStakeText}`;
        }
    }

    /**
     * Calculates the minimum and maximum scroll values based on the number of stakes and buttons per row.
     * @param {number[]} stakes - An array of stake values.
     * @param {number} buttonsPerRow - The number of buttons per row.
     * @returns {number[]} An array containing the minimum and maximum scroll values, in that order.
     */
    protected getMinMaxScroll(stakes: number[], buttonsPerRow: number): number[] {
        let numRows = Math.floor(stakes.length / buttonsPerRow);
        let minScrollY = 0;
        let maxScrollY = 0;

        if (stakes.length % buttonsPerRow !== 0) {
            ++numRows;
        }

        const numDisplayedRows = RenderOrientation.isLandscape() ? 4 : 6;
        this.displayHeight = (this.stakeButton.getTrueBounds().height + this.buttonGapAmount) * numDisplayedRows;

        if (numRows <= numDisplayedRows) {
            minScrollY = 0;
        } else {
            minScrollY = (numRows - numDisplayedRows) * (this.stakeButton.getTrueBounds().height + this.buttonGapAmount);
        }

        maxScrollY = (numRows - 1) * (this.stakeButton.getTrueBounds().height + this.buttonGapAmount);

        return [minScrollY, maxScrollY];
    }

    /**
     * Calculates the top and bottom positions of the scroll bar within the scroll track.
     * @param {Sprite} scrollTrack - The scroll track sprite.
     * @param {Sprite} scrollBar - The scroll bar sprite.
     * @returns {number[]} An array containing the top and bottom positions of the scroll bar.
     */
    protected getScrollBarTopBottom(scrollTrack: Sprite, scrollBar: Sprite): number[] {
        const orientation = RenderOrientation.orientation;
        const barPos = scrollBar.dualPosition[orientation];
        const trackPos = scrollTrack.dualPosition[orientation];

        return [barPos.y, barPos.y + (trackPos.height - barPos.height)];
    }

    /**
     * Removes the specified elements from the layer and returns an array of the removed elements.
     * @param {string[]} elements - An array of element names to be removed.
     * @returns {any[]} An array containing the removed elements.
     */
    protected removeElementsFromLayer(elements: string[]): any[] {
        const removedElements = [];
        elements.forEach((eachElement) => {
            if (Object.prototype.hasOwnProperty.call(this.layer.allElements, eachElement)) {
                removedElements.push(this.layer.allElements[eachElement]);
                this.layer.remove(this.layer.allElements[eachElement], eachElement);
            }
        });

        return removedElements;
    }

    /**
     * Sets the enabled status of a button based on its position within the bounds of the menu.
     * @param {ButtonElement} btn - The button to set the enabled status for.
     */
    protected setButtonEnabledStatus(btn: ButtonElement) {
        const orientation = RenderOrientation.orientation;
        const btnHeight = this.stakeButton.getTrueBounds().height;

        const isOutsideBounds =
            btn.dualPosition[orientation].y < this.stakeButton.dualPosition[orientation].y - btnHeight / 2 ||
            btn.dualPosition[orientation].y + btnHeight / 2 > this.stakeButton.dualPosition[orientation].y + this.displayHeight;

        btn.setEnabled(!isOutsideBounds);
    }

    /**
     * Generates stake buttons for the bet menu based on the given stakes, current stake value, and buttons per row.
     * @param {number[]} stakes - An array of stake values.
     * @param {number} currentStake - The current stake value.
     * @param {number} buttonsPerRow - The number of buttons per row.
     */
    protected generateStakeButtons(stakes: number[], currentStake: number, buttonsPerRow: number): void {
        const orientation = RenderOrientation.orientation;
        let currX = this.stakeButton.dualPosition[orientation].x;
        let currY = this.stakeButton.dualPosition[orientation].y;

        let buttonsAdded = -1;
        const shouldDisplayTrailingDecimals = gameiomModel.shouldDisplayCredits() ? false : gameiomModel.displayWholeStakeAsDecimal;

        // Generate buttons for each enabled settings button in the gameiom model.
        stakes.forEach((eachStake) => {
            const buttonName = `${this.stakeButtonPrefix}${eachStake}`;

            // Clone the generic button and add appropriate callback.
            const clonedButton: ButtonElement = this.stakeButton.clone();
            clonedButton.setLabelText(
                Services.get(CurrencyService).format(eachStake, shouldDisplayTrailingDecimals, gameiomModel.shouldDisplayCredits())
            );
            clonedButton.on(ButtonEvent.CLICK, () => {
                if (Math.abs(this.scrolledAmount) < this.scrollEpsilon) {
                    Services.get(SoundService).customEvent(SoundEvent.default_stake_button);
                    const stakeOptions = slotModel.read().bet.creditsPerLine;
                    stakeOptions.currentValue = eachStake;
                    slotModel.write({ bet: { creditsPerLine: stakeOptions } });
                    Services.get(SoundService).event(SoundEvent.button_press_NAME, clonedButton.name);
                    uiFlags.closeMenus();
                }
            });

            // Remove the event so that sound is not played on initial press
            clonedButton.unregisterPressSound();

            const buttonRect = clonedButton.getTrueBounds();

            if (++buttonsAdded !== 0) {
                if (buttonsAdded % buttonsPerRow === 0) {
                    currX = this.stakeButton.dualPosition[orientation].x;
                    currY += buttonRect.height + this.buttonGapAmount;
                } else {
                    currX += buttonRect.width + this.buttonGapAmount;
                }

                clonedButton.dualPosition[orientation].x = currX;
                clonedButton.dualPosition[orientation].y = currY;
            }

            // Button name should include the stake value.
            clonedButton.name = buttonName;
            this.addScrollEventsToDisplayObject(clonedButton);

            // Enable/disable button based on if it is in the active display area
            this.setButtonEnabledStatus(clonedButton);

            // Active stake gets highlighted.
            if (eachStake == currentStake) {
                clonedButton.setSelected(true);
            }

            // Mask the button space to create correct scrolling look.
            clonedButton.mask = this.layer.getSprite(this.menuMaskName);

            // Add button properly to the layer.
            this.layer.add(clonedButton);
            this.layer.addButton(clonedButton.name, clonedButton);
        });
    }

    /**
     * Turns on scrolling functionality by adding scroll events to the menu background sprite
     * and registering the scroll wheel listener.
     */
    protected turnOnScrolling(): void {
        const menuSprite = this.layer.getSprite(this.menuBackgroundName);
        this.addScrollEventsToDisplayObject(menuSprite);

        this.scrollWheelCallback = this.handleScrollWheel.bind(this);
        window.addEventListener("wheel", this.scrollWheelCallback);
    }

    /**
     * Adds scroll events to the provided display object for scrolling functionality.
     * @param {Sprite | ButtonElement} displayObj - The display object to which scroll events will be added.
     * @param {boolean} isScrollClicked - Set to true if this is a direct click on the scroll bar.
     */
    protected addScrollEventsToDisplayObject(displayObj: Sprite | ButtonElement, isScrollClicked = false): void {
        displayObj
            .on("pointerdown", (event) => {
                this.activeScrollObject = displayObj;
                this.scrolledAmount = 0;
                this.previousY = event.data.global.y;
            })
            .on("pointerup", () => {
                this.activeScrollObject = undefined;
            })
            .on("pointerupoutside", () => {
                this.activeScrollObject = undefined;
            })
            .on("pointermove", (event) => {
                if (this.activeScrollObject?.name === displayObj?.name) {
                    const delta = event.data.global.y - this.previousY;

                    // pointermove events come through on mobile even with no delta. Exit early in this case so we don't consider
                    // it as an actual scroll that takes place.
                    if (delta == 0) {
                        return;
                    }

                    this.previousY = event.data.global.y;

                    this.moveButtonsByAmount(delta, isScrollClicked);
                }
            });
    }

    /**
     * Sets the opacity of fade sprites based on the provided percentage.
     * @param {number} percentage - The percentage value ranging from 0 to 1.
     */
    protected setFadesOpacity(percentage: number) {
        const tweenRange = 0.05;
        this.fadeSpriteTop.alpha = this.lerp(0, 1, this.clamp(percentage, 0, tweenRange) / tweenRange);
        this.fadeSpriteBottom.alpha = this.lerp(1, 0, this.clamp(percentage - 1 + tweenRange, 0, tweenRange) / tweenRange);
    }

    /**
     * Handles the scroll wheel event and moves the buttons based on the scroll delta.
     * @param {WheelEvent} event - The scroll wheel event object.
     */
    protected handleScrollWheel(event: WheelEvent) {
        this.moveButtonsByAmount(event.deltaY / -2);
    }

    /**
     * Performs linear interpolation (lerp) between two values based on a percentage.
     * @param {number} first - The first value.
     * @param {number} second - The second value.
     * @param {number} percent - The percentage used for interpolation, ranging from 0 to 1.
     * @returns {number} The interpolated value.
     */
    protected lerp(first: number, second: number, percent: number): number {
        return first * (1 - percent) + second * percent;
    }

    /**
     * Clamps a value between a minimum and maximum range.
     * @param {number} value - The value to be clamped.
     * @param {number} min - The minimum value of the range.
     * @param {number} max - The maximum value of the range.
     * @returns {number} The clamped value within the specified range.
     */
    protected clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }

    /**
     * Moves the buttons vertically by the specified delta amount.
     * @param {number} delta - The amount by which to move the buttons vertically.
     * @param {boolean} isScrollClicked - Set to true if this is a direct click on the scroll bar.
     */
    protected moveButtonsByAmount(delta: number, isScrollClicked = false) {
        const orientation = RenderOrientation.orientation;

        let minY: number = undefined;
        let maxY: number = undefined;

        // Obtain the minimum and maximum Y values from among all buttons
        for (const key in this.layer.allElements) {
            if (key.includes(this.stakeButtonPrefix)) {
                const btn = this.layer.allElements[key] as ButtonElement;
                if (minY == undefined || minY > btn.dualPosition[orientation].y) {
                    minY = btn.dualPosition[orientation].y;
                }

                if (maxY == undefined || maxY < btn.dualPosition[orientation].y) {
                    maxY = btn.dualPosition[orientation].y;
                }
            }
        }

        let percentage = 0;
        let buttonDelta = delta;

        if (this.scrollBarSprite) {
            if (!isScrollClicked) {
                delta = -delta;
            }

            const scrollMinDelta = this.scrollBarTopY - this.scrollBarSprite.dualPosition[orientation].y;
            const scrollMaxDelta = this.scrollBarBottomY - this.scrollBarSprite.dualPosition[orientation].y;

            // Disallow scroll if the delta would take us beyond the allowable scroll range
            delta = this.clamp(delta, scrollMinDelta, scrollMaxDelta);

            this.scrollBarSprite.dualPosition[orientation].y += delta;

            // Set the scroll bar position based on percentage moved
            percentage =
                (this.scrollBarSprite.dualPosition[orientation].y - this.scrollBarTopY) / (this.scrollBarBottomY - this.scrollBarTopY);

            const deltaPercentage = delta / (this.scrollBarBottomY - this.scrollBarTopY);
            buttonDelta = this.lerp(0, -this.minScrollY, deltaPercentage);
        } else {
            const buttonMinDelta = this.stakeButton.dualPosition[orientation].y - this.minScrollY - minY;
            const buttonMaxDelta = this.stakeButton.dualPosition[orientation].y + this.maxScrollY - maxY;

            // Disallow scroll if the delta would take us beyond the allowable scroll range
            delta = this.clamp(delta, buttonMinDelta, buttonMaxDelta);

            // Set the scroll bar position based on percentage moved
            percentage = (minY + delta - this.stakeButton.dualPosition[orientation].y) / -this.minScrollY;

            buttonDelta = delta;
        }

        // Set opacity of the fades based on how close we are to being at one of the ends of the scroll bar
        this.setFadesOpacity(percentage);

        this.scrolledAmount += buttonDelta;

        // Scroll the buttons by the delta amount
        for (const key in this.layer.allElements) {
            if (key.includes(this.stakeButtonPrefix)) {
                const btn = this.layer.allElements[key] as ButtonElement;
                btn.dualPosition[orientation].y += buttonDelta;

                this.setButtonEnabledStatus(btn);
            }
        }
    }
}
